// ============================================================================
// Vehicle detail (view c) — finance drill-in. Cost tiles by category + miles/mo,
// compliance rows (derived chips), mileage-history mini-chart, recent costs, and
// a flag-don't-block review queue (needs_review costs rendered as amber rows an
// admin can clear). RLS scopes data: the assigned driver sees only their own
// vehicle + costs; fleet-admins see everything and can add/edit.
// ============================================================================
import { isFleetAdmin } from '../../shared/role-gates';
import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Flag, Gauge, Plus, Tag, Zap } from 'lucide-react';
import { PageHeader, EmptyState, ErrorState, Skeleton } from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { useToast } from '../../shared/components';
import { complianceChip, chipClasses } from './compliance';
import {
  useVehicleDetail,
  avgMilesPerMonth,
  fmtMoney,
  fmtDate,
  clearCostReview,
  firstOfMonthISO,
  type CostRow,
} from './hooks/use-fleet-data';
import { AssetCostModal } from './asset-modals';
import { VehicleStatusSheet } from './vehicle-actions';
import { vehicleStatusLabel, vehicleStatusTone } from './vehicle-status';


export default function VehicleDetail() {
  const { vehicleId } = useParams();
  const { profile } = useAuth();
  const { success, error: toastErr } = useToast();
  const { vehicle, readings, costs, nameMap, loading, error, refetch } = useVehicleDetail(vehicleId);
  const [costModal, setCostModal] = useState<{ open: boolean; row: CostRow | null }>({ open: false, row: null });
  const [statusSheet, setStatusSheet] = useState(false);
  const isAdmin = isFleetAdmin(profile);

  const mtd = useMemo(() => costs.filter((c) => c.incurred_on >= firstOfMonthISO()), [costs]);
  const byCategory = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of mtd) m.set(c.cost_category, (m.get(c.cost_category) ?? 0) + Number(c.amount));
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [mtd]);
  const mtdTotal = useMemo(() => mtd.reduce((s, c) => s + Number(c.amount), 0), [mtd]);
  const avg = useMemo(() => avgMilesPerMonth(readings), [readings]);
  const reviewQueue = useMemo(() => costs.filter((c) => c.needs_review), [costs]);

  async function clear(costId: string) {
    try {
      await clearCostReview(costId);
      success('Cleared from review.');
      refetch();
    } catch {
      toastErr('Could not clear the flag.');
    }
  }

  if (loading) return <div className="mx-auto max-w-5xl p-6 space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-32 w-full" /></div>;
  if (error) return <ErrorState body={error} onRetry={refetch} />;
  if (!vehicle) return <EmptyState title="Vehicle not found" body="This vehicle may have been removed or you don't have access." />;

  const reg = complianceChip(vehicle.registration_exp);
  const ins = complianceChip(vehicle.insurance_exp);
  const emi = vehicle.emissions_exp ? complianceChip(vehicle.emissions_exp) : null;
  const odo = readings[0]?.odometer ?? vehicle.mileage ?? null;

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-4 md:p-6">
      <Link to="/fleet" className="inline-flex items-center gap-1 text-sm text-tcs-text-secondary hover:text-tcs-text"><ArrowLeft className="h-4 w-4" /> Fleet & Assets</Link>
      <PageHeader
        eyebrow={<span className="font-mono">{vehicle.vehicle_id}</span>}
        title={
          <span className="inline-flex items-center gap-2">
            {vehicle.powertrain === 'ev' && <Zap className="h-5 w-5 text-st-ok-fg" />}
            {vehicle.make_model ?? vehicle.vehicle_id}
            {/* Status was a fragment of the meta STRING until 2026-08-24, which
                is why nobody could find it, let alone change it. Same chip
                palette the equipment card uses for machine status. */}
            <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${chipClasses(vehicleStatusTone(vehicle.status))}`}>
              {vehicleStatusLabel(vehicle.status)}
            </span>
          </span>
        }
        meta={`${vehicle.license_plate ?? 'no plate'}${vehicle.plate_state ? ` (${vehicle.plate_state})` : ''} · ${vehicle.assigned_employee_eid ? nameMap.get(vehicle.assigned_employee_eid) ?? vehicle.assigned_employee_eid : 'unassigned'}`}
        actions={isAdmin ? (
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setStatusSheet(true)} className="inline-flex items-center gap-1.5 rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-sm font-medium text-tcs-text hover:bg-tcs-surface"><Tag className="h-4 w-4" /> Change status</button>
            <button type="button" onClick={() => setCostModal({ open: true, row: null })} className="inline-flex items-center gap-1.5 rounded-md bg-tcs-inverse px-3 py-1.5 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse/90"><Plus className="h-4 w-4" /> Add cost</button>
          </div>
        ) : undefined}
      />

      {/* cost tiles + miles/mo */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-tcs-border bg-tcs-bg p-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">MTD cost</div>
          <div className="mt-1 font-mono text-2xl font-semibold text-tcs-text">{fmtMoney(mtdTotal)}</div>
        </div>
        <div className="rounded-xl border border-tcs-border bg-tcs-bg p-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">Odometer</div>
          <div className="mt-1 flex items-center gap-1 font-mono text-2xl font-semibold text-tcs-text"><Gauge className="h-4 w-4 text-tcs-text-muted" />{odo != null ? odo.toLocaleString() : '—'}</div>
        </div>
        <div className="rounded-xl border border-tcs-border bg-tcs-bg p-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">Avg mi / mo</div>
          <div className="mt-1 font-mono text-2xl font-semibold text-tcs-text">{avg != null ? avg.toLocaleString() : '—'}</div>
        </div>
        <div className="rounded-xl border border-tcs-border bg-tcs-bg p-3">
          <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">Powertrain</div>
          <div className="mt-1 text-lg font-semibold capitalize text-tcs-text">{vehicle.powertrain ?? '—'}{vehicle.has_home_charger && <span className="ml-1 text-xs font-normal text-tcs-text-muted">· home charger</span>}</div>
        </div>
      </div>

      {/* cost by category */}
      {byCategory.length > 0 && (
        <div className="rounded-lg border border-tcs-border bg-tcs-raised p-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">Cost this month by category</div>
          <div className="flex flex-wrap gap-2">
            {byCategory.map(([cat, amt]) => (
              <span key={cat} className="inline-flex items-center gap-1.5 rounded-full border border-tcs-border bg-tcs-surface px-2.5 py-1 text-xs text-tcs-text">
                <span className="capitalize">{cat.replace('_', ' ')}</span>
                <span className="font-mono font-semibold">{fmtMoney(amt)}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* compliance rows */}
      <div className="rounded-lg border border-tcs-border bg-tcs-raised p-4">
        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">Compliance</div>
        <div className="divide-y divide-tcs-border">
          <ComplianceRow label="Registration" date={vehicle.registration_exp} level={reg.level} labelText={reg.label} />
          <ComplianceRow label="Insurance" date={vehicle.insurance_exp} level={ins.level} labelText={ins.label} sub={vehicle.insurance_provider} />
          {emi ? (
            <ComplianceRow label="Emissions" date={vehicle.emissions_exp} level={emi.level} labelText={emi.label} />
          ) : (
            <div className="flex items-center justify-between py-2 text-sm"><span className="text-tcs-text">Emissions</span><span className="text-tcs-text-muted">n/a</span></div>
          )}
        </div>
      </div>

      {/* mileage history mini-chart */}
      <div className="rounded-lg border border-tcs-border bg-tcs-raised p-4">
        <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">Mileage history</div>
        <MileageChart readings={readings.map((r) => ({ read_on: r.read_on, odometer: r.odometer }))} />
      </div>

      {/* needs_review queue */}
      {reviewQueue.length > 0 && (
        <div className="rounded-lg border border-st-warn-border bg-st-warn-bg p-4">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-st-warn-fg-strong"><Flag className="h-3.5 w-3.5" /> Needs review ({reviewQueue.length}) — flag only, never blocks</div>
          <div className="space-y-1.5">
            {reviewQueue.map((c) => (
              <div key={c.cost_id} className="flex items-center justify-between gap-3 rounded-md border border-st-warn-border bg-tcs-raised px-3 py-2 text-sm">
                <div className="min-w-0">
                  <span className="font-medium capitalize text-tcs-text">{c.cost_category.replace('_', ' ')}</span>
                  <span className="ml-2 font-mono">{fmtMoney(c.amount)}</span>
                  <span className="ml-2 text-tcs-text-muted">{fmtDate(c.incurred_on)}{c.vendor ? ` · ${c.vendor}` : ''}</span>
                </div>
                {isAdmin && (
                  <div className="flex shrink-0 gap-1.5">
                    <button type="button" onClick={() => setCostModal({ open: true, row: c })} className="rounded border border-tcs-border bg-tcs-raised px-2 py-1 text-xs text-tcs-text hover:bg-tcs-surface">Edit</button>
                    <button type="button" onClick={() => clear(c.cost_id)} className="rounded border border-st-warn-border-strong bg-st-warn-bg-strong px-2 py-1 text-xs font-medium text-st-warn-fg-strong hover:bg-st-warn-bg-strong">Clear</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* recent costs */}
      <div className="rounded-lg border border-tcs-border bg-tcs-raised">
        <div className="border-b border-tcs-border px-4 py-3 text-xs font-semibold uppercase tracking-wide text-tcs-text-muted">Recent costs</div>
        {costs.length === 0 ? (
          <div className="p-4 text-sm text-tcs-text-muted">No costs recorded yet.</div>
        ) : (
          <table className="min-w-full divide-y divide-tcs-border text-sm">
            <tbody className="divide-y divide-tcs-border">
              {costs.slice(0, 30).map((c) => (
                <tr key={c.cost_id} className="hover:bg-tcs-surface">
                  <td className="px-4 py-2 capitalize text-tcs-text">{c.cost_category.replace('_', ' ')}{c.is_capital && <span className="ml-1.5 rounded bg-tcs-surface px-1 py-0.5 text-[10px] uppercase text-tcs-text-muted">capital</span>}</td>
                  <td className="px-4 py-2 text-tcs-text-secondary">{c.vendor ?? '—'}</td>
                  <td className="px-4 py-2 text-tcs-text-secondary">{fmtDate(c.incurred_on)}</td>
                  <td className="px-4 py-2 text-right font-mono tabular-nums">{fmtMoney(c.amount)}</td>
                  <td className="px-4 py-2 text-right">{isAdmin && <button type="button" onClick={() => setCostModal({ open: true, row: c })} className="rounded border border-tcs-border bg-tcs-raised px-2 py-0.5 text-xs text-tcs-text hover:bg-tcs-surface">Edit</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {costModal.open && (
        <AssetCostModal cost={costModal.row} vehicleId={vehicle.vehicle_id} onClose={() => setCostModal({ open: false, row: null })} onSaved={refetch} />
      )}
      {statusSheet && (
        <VehicleStatusSheet vehicle={vehicle} onClose={() => setStatusSheet(false)} onSaved={refetch} />
      )}
    </div>
  );
}

function ComplianceRow({ label, date, level, labelText, sub }: { label: string; date: string | null; level: ReturnType<typeof complianceChip>['level']; labelText: string; sub?: string | null }) {
  return (
    <div className="flex items-center justify-between py-2 text-sm">
      <div>
        <span className="text-tcs-text">{label}</span>
        {sub && <span className="ml-2 text-xs text-tcs-text-muted">{sub}</span>}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-tcs-text-secondary">{fmtDate(date)}</span>
        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${chipClasses(level)}`}>{labelText}</span>
      </div>
    </div>
  );
}

function MileageChart({ readings }: { readings: { read_on: string; odometer: number }[] }) {
  if (readings.length < 2) {
    return <div className="text-sm text-tcs-text-muted">Not enough readings yet to chart a trend.</div>;
  }
  const pts = [...readings].sort((a, b) => a.read_on.localeCompare(b.read_on));
  const W = 600, H = 120, P = 8;
  const xs = pts.map((p) => new Date(p.read_on).getTime());
  const ys = pts.map((p) => p.odometer);
  const xMin = Math.min(...xs), xMax = Math.max(...xs);
  const yMin = Math.min(...ys), yMax = Math.max(...ys);
  const nx = (x: number) => (xMax === xMin ? P : P + ((x - xMin) / (xMax - xMin)) * (W - 2 * P));
  const ny = (y: number) => (yMax === yMin ? H / 2 : H - P - ((y - yMin) / (yMax - yMin)) * (H - 2 * P));
  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${nx(xs[i]).toFixed(1)} ${ny(p.odometer).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" style={{ height: 120 }}>
      <path d={d} fill="none" stroke="currentColor" strokeWidth={2} className="text-tcs-text" />
      {pts.map((p, i) => <circle key={i} cx={nx(xs[i])} cy={ny(p.odometer)} r={2.5} className="fill-tcs-text" />)}
    </svg>
  );
}
